import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { MdOutgoingMail } from 'react-icons/md';
import ThreePOutlinedIcon from '@mui/icons-material/ThreePOutlined';
import type { GridProps } from '@mui/material';
import { Stack, Typography, useMediaQuery, useTheme } from '@mui/material';

import type { WidgetConfig } from 'src/types/dashboard';
import SpeedometerChart from 'src/components/dashboard/charts/SpeedometerChart';
import ComparisonPill from 'src/components/dashboard/DashboardPage/components/ComparisonPill/ComparisonPill';
import { useSpeedometerCalculations } from 'src/components/dashboard/DashboardPage/widgets/SpeedometerWidget/hooks';
import WidgetLegend from 'src/components/dashboard/DashboardPage/widgets/SpeedometerWidget/WidgetLegend';
import type { Aggregate } from 'src/components/followUp/common/types';

import { styles } from './WidgetViewCommonWidget.styles';

export const SPEEDOMETER_WIDGET_TEST_ID = 'speedometer-widget';

export interface WidgetViewSpeedometerWidgetProps extends GridProps {
  data?: Aggregate;
  comparisonData?: Aggregate;
  widgetConfig?: WidgetConfig;
}

const WidgetViewSpeedometerWidget: FC<WidgetViewSpeedometerWidgetProps> = ({
  data,
  comparisonData,
}) => {
  const theme = useTheme();
  const upXl = useMediaQuery(theme.breakpoints.up('xl'), { noSsr: true });
  const { t } = useTranslation('followUp');

  const {
    absoluteValue,
    percentage,
    percentageDifference,
    absoluteDifference,
  } = useSpeedometerCalculations({
    data,
    comparisonData,
  });

  return (
    <Stack
      direction="column"
      spacing={1}
      sx={styles.wrapper}
      data-test-id={SPEEDOMETER_WIDGET_TEST_ID}
    >
      <Stack direction="column" spacing={1}>
        <Stack
          direction="row"
          sx={{
            justifyContent: 'center',
          }}
        >
          <SpeedometerChart
            percentage={percentage}
            height={280}
            color={theme.colors.secondary[500]}
          >
            <Typography
              component="span"
              sx={{
                fontSize: upXl ? '2.75rem' : '2.25rem',
                fontWeight: 600,
                lineHeight: 'normal',
                mb: percentageDifference === undefined ? 0 : 3,
                position: 'relative',
              }}
            >
              {Math.round(absoluteValue)}
              <span style={{ marginLeft: 8 }}>%</span>
              {percentageDifference !== undefined && (
                <ComparisonPill
                  value={percentageDifference}
                  component="span"
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                  }}
                />
              )}
            </Typography>
          </SpeedometerChart>
        </Stack>
        <WidgetLegend
          labelValuePairs={[
            {
              label: (
                <>
                  <MdOutgoingMail size={18} />
                  {t('statistics.invitees')}
                </>
              ),
              value: data?.totalObjects,
              key: 'Invitees',
              comparisonValue: absoluteDifference?.totalObjects,
            },
            {
              label: (
                <>
                  <ThreePOutlinedIcon sx={{ fontSize: 18 }} />
                  {t('statistics.respondents')}
                </>
              ),
              value: data?.value,
              key: 'Respondents',
              comparisonValue: absoluteDifference?.value,
            },
          ]}
        />
      </Stack>
    </Stack>
  );
};

export default WidgetViewSpeedometerWidget;
